import classNames from 'classnames';
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { appRoutes } from 'config';
import { marketing } from 'config/global';
import { useLoginModalTrigger } from 'hooks/useLoginModalTrigger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faApple,
  faAndroid,
  faGooglePlay,
} from '@fortawesome/free-brands-svg-icons';
import {
  faDisplay,
  faGlobe,
  // TODO(SP-675): Chrome extension temporarily hidden for launch —
  // https://mindfeed.atlassian.net/browse/SP-675
  // faPuzzlePiece,
} from '@fortawesome/pro-regular-svg-icons';

type PlatformId = 'ios' | 'android' | 'macos' | 'web' | 'chrome';

// Static, non-translatable meta. Title / description / CTA "top" line are
// pulled from i18n at render time (keys: `<id>Title`, `<id>Description`,
// `<id>CtaTop` under pages.home.section13).
interface PlatformConfig {
  id: PlatformId;
  tabLabel: string;
  tabIcon: IconDefinition;
  ctaIcon: IconDefinition;
  ctaBottom: string;
  href: string;
}

const PLATFORMS: PlatformConfig[] = [
  {
    id: 'ios',
    tabLabel: 'iOS',
    tabIcon: faApple,
    ctaIcon: faApple,
    ctaBottom: 'App Store',
    href: marketing.appStoreUri,
  },
  {
    id: 'android',
    tabLabel: 'Android',
    tabIcon: faAndroid,
    ctaIcon: faGooglePlay,
    ctaBottom: 'Google Play',
    href: marketing.googlePlayUri,
  },
  {
    id: 'macos',
    tabLabel: 'macOS',
    tabIcon: faDisplay,
    ctaIcon: faApple,
    ctaBottom: 'macOS',
    href: marketing.macAppStoreUri,
  },
  {
    id: 'web',
    tabLabel: 'Web',
    tabIcon: faGlobe,
    ctaIcon: faGlobe,
    ctaBottom: 'Web app',
    href: appRoutes.LOGIN,
  },
  // TODO(SP-675): Chrome extension temporarily hidden for launch —
  // https://mindfeed.atlassian.net/browse/SP-675
  /*
  {
    id: 'chrome',
    tabLabel: 'Chrome extension',
    tabIcon: faPuzzlePiece,
    ctaIcon: faPuzzlePiece,
    ctaBottom: 'Chrome',
    href: '#',
  },
  */
];

export const Section13 = () => {
  const t = useTranslations();
  const [activeId, setActiveId] = useState<PlatformId>('ios');
  const active = PLATFORMS.find((p) => p.id === activeId) ?? PLATFORMS[0];
  const ctaTop = t(`pages.home.section13.${active.id}CtaTop`);
  const openLoginModal = useLoginModalTrigger(active.href);

  return (
    <section id="apps" className={styles.section}>
      <div className={styles.wrap}>
        <div className={styles.eyebrow}>
          {t('pages.home.section13.eyebrow')}
        </div>
        <h2 className={styles.title}>
          {t.rich('pages.home.section13.title', {
            highlight: (chunks) => (
              <span className={styles.emphasis}>{chunks}</span>
            ),
          })}
        </h2>
        <p className={styles.description}>
          {t('pages.home.section13.description')}
        </p>

        {/* Platform switcher */}
        <div className={styles.tabs}>
          {PLATFORMS.map((platform) => {
            const isActive = platform.id === activeId;
            return (
              <button
                key={platform.id}
                type="button"
                aria-pressed={isActive}
                onClick={() => setActiveId(platform.id)}
                className={classNames(
                  styles.tab.base,
                  isActive ? styles.tab.active : styles.tab.inactive,
                )}
              >
                <FontAwesomeIcon
                  icon={platform.tabIcon}
                  className={styles.tab.icon}
                />
                {platform.tabLabel}
              </button>
            );
          })}
        </div>

        {/* Selected platform detail */}
        <div className={styles.panel.container}>
          <p className={styles.panel.text}>
            <span className={styles.panel.title}>
              {t(`pages.home.section13.${active.id}Title`)}
            </span>{' '}
            {t(`pages.home.section13.${active.id}Description`)}
          </p>
          <a
            href={active.href}
            className={styles.cta.button}
            onClick={openLoginModal}
            target={active.href.startsWith('http') ? '_blank' : undefined}
            rel={
              active.href.startsWith('http') ? 'noopener noreferrer' : undefined
            }
          >
            <FontAwesomeIcon
              icon={active.ctaIcon}
              className={styles.cta.icon}
            />
            <span className={styles.cta.label}>
              <span className={styles.cta.top}>{ctaTop}</span>
              <span className={styles.cta.bottom}>{active.ctaBottom}</span>
            </span>
          </a>
        </div>
      </div>
    </section>
  );
};

const styles = {
  // scroll-mt offsets the #apps anchor for the fixed header stack
  // (announcement bar 36px + nav 64px) so it doesn't land under the header.
  section: 'marketing-dark:bg-mf-navy py-[74px] text-center scroll-mt-[110px]',
  wrap: 'mx-auto max-w-[1000px] px-6 lg:px-10',
  eyebrow:
    'mb-4 text-[13px] font-semibold uppercase tracking-[0.15em] text-mf-blue marketing-dark:text-mf-coral',
  title:
    'mx-auto max-w-[760px] text-4xl font-extrabold leading-tight tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white sm:text-5xl lg:text-[56px]',
  emphasis: 'text-mf-blue marketing-dark:text-mf-coral',
  description:
    'mx-auto mt-6 max-w-[680px] text-lg leading-[1.7] text-mf-gray-600 marketing-dark:text-white/64',
  tabs: 'mt-10 flex flex-wrap justify-center gap-3',
  tab: {
    // Pill with a hover lift; the selected platform turns solid ink (light
    // theme) / solid white (dark theme).
    base: 'inline-flex items-center gap-2 rounded-full px-5 py-3 text-[15px] font-semibold transition-all duration-200 hover:-translate-y-0.5',
    active: classNames(
      'border border-mf-off-black-600 bg-mf-off-black-600 text-white',
      'marketing-dark:border-0 marketing-dark:bg-white marketing-dark:text-mf-off-black-600',
      'marketing-dark:shadow-[0_8px_22px_rgba(0,0,0,0.25)]',
    ),
    inactive: classNames(
      'border border-[#DADADA] bg-white text-mf-gray-600 hover:bg-[#F4F4F4] hover:text-mf-off-black-600',
      'marketing-dark:border-white/10 marketing-dark:bg-white/[0.04] marketing-dark:text-white/80',
      'marketing-dark:hover:bg-white/[0.08] marketing-dark:hover:text-white',
    ),
    icon: 'h-4 w-4',
  },
  panel: {
    container: 'mt-12',
    text: 'mx-auto max-w-[620px] text-lg leading-[1.6] text-mf-gray-600 marketing-dark:text-white/55',
    title: 'font-bold text-mf-off-black-600 marketing-dark:text-white',
  },
  cta: {
    button: classNames(
      'mt-8 inline-flex items-center gap-3 rounded-2xl px-6 py-3 text-left transition-all duration-200 hover:-translate-y-0.5',
      'bg-mf-off-black-600 text-white hover:shadow-[0_8px_20px_rgba(20,30,60,0.16)]',
      'marketing-dark:bg-white marketing-dark:text-mf-off-black-600',
      'marketing-dark:shadow-[0_10px_30px_rgba(0,0,0,0.25)]',
      'marketing-dark:hover:shadow-[0_16px_40px_rgba(0,0,0,0.3)]',
    ),
    icon: 'h-7 w-7',
    label: 'flex flex-col items-start leading-tight',
    top: 'text-[11px] font-medium text-white/70 marketing-dark:text-mf-off-black-400',
    bottom: '-mt-0.5 text-[17px] font-bold',
  },
};
